
import styled from 'styled-components';
import Link from 'next/link';
import { BagHandle } from '@styled-icons/ionicons-sharp/BagHandle';
import { useTranslations } from 'next-intl';
import { Tiles } from '@rebass/layout';

import config from '@/config';
import Loader from '@/components/loader';
import Button from '@/components/button';
import BrandCard from '@/components/brand-card';

const Container = styled.div`
  flex: 1;
`;

const BrandGridWrapper = styled(Tiles)`
  a {
    display: block;
    text-decoration: none;
    margin: auto;
  }
`;

const Message = styled.div`
  margin: 2em 0;
  text-align: center;

  p {
    margin: 0;
  }

  strong {
    color: ${props => props.theme.colors.accent};
  }
`;

const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 2em 0;
`;

const Actions = styled.div`
  align-items: center;
  display: flex;

  a {
    color: ${props => props.theme.colors.primary};
    margin-left: 1em;
    text-decoration: underline;
  }
`;

const LoadingState = styled.div`
  height: 200px;
  margin: 50px 0;
  position: relative;
`;

const ShoppingBag = styled(BagHandle)`
  color: ${({ theme }) => theme.colors.primary};
  margin: auto;
  max-width: 6em;
`;

const OtherBrandsTitle = styled.h3`
  border-bottom: 1px solid rgba(0, 0, 0, .05);
  margin-bottom: 1em;
  padding-bottom: .5em;
`;

const OtherBrands = styled.div`
  margin-top: 2em;
`;

const CountryFlag = styled.div`
  align-items: center;
  background: ${props => props.theme.colors.background.primary};
  border-radius: 0 .35em 0 .5em;
  box-shadow: ${props => props.theme.shadows.primary};
  display: flex;
  font-size: 1.25em;
  line-height: 1;
  padding: .25em .35em;
  position: absolute;
  right: 0;
  top: 0;

  span {
    color: ${props => props.theme.colors.typography.primary};
    font-size: .55em;
    margin-left: .75em;
    max-width: 8em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const BrandGrid = ({
  brands,
  category,
  country,
  loading,
  onReset,
  otherBrands,
  showEmptyState,
  showOtherBrands
}) => {
  const otherGiftcards = Array.isArray(otherBrands) && otherBrands.filter(brand => brand.type === 'giftcard');
  const otherTopups = Array.isArray(otherBrands) && otherBrands.filter(brand => brand.type !== 'giftcard');
  const t = useTranslations('brandGrid');
  const cT = useTranslations('categories');

  return (
    <Container>
      {showEmptyState && !loading && !brands.length &&
        <EmptyState>
          <ShoppingBag />
          <Message>
            {(!showOtherBrands || !otherBrands.length) &&
              <>
                {!category &&
                  <p>
                    {t.rich('noResultsInCountry', {
                      country: country.name,
                      strong: children => <strong>{children}</strong>
                    })}
                  </p>
                }
                {category &&
                  <p>{t.rich('noResultsInCategory', {
                    country: country.name,
                    category: cT(category.slug || category.name),
                    strong: children => <strong>{children}</strong>
                  })}</p>
                }
              </>
            }
            {showOtherBrands && !!otherBrands.length &&
              <>
                <p>
                  {t.rich('noResultsInCountry', {
                    country: country.name,
                    strong: children => <strong>{children}</strong>
                  })}
                </p>
                <p>{t('resultsInOtherCountries')}</p>
              </>
            }
          </Message>
          {(!showOtherBrands || !otherBrands.length) &&
            <Actions>
              <Button onClick={onReset} primary>{t('browseAll')}</Button>
              <Link href={config.addYourBusinessUrl} passHref className="button">
                {t('addBusiness')}
              </Link>
              <Link href={config.brandRequestUrl} passHref className="button">
                {t('requestBrand')}
              </Link>
            </Actions>
          }
        </EmptyState>
      }

      {loading &&
        <LoadingState>
          <Loader />
        </LoadingState>
      }

      {!loading && !!brands.length &&
        <BrandGridWrapper columns={[2, 3, 3, 4]}>
          {brands.map(brand => <BrandCard key={brand.id} brand={brand} />)}
        </BrandGridWrapper>
      }

      {!loading && showOtherBrands && !!otherGiftcards.length &&
        <OtherBrands>
          {!!brands.length &&
            <OtherBrandsTitle>{t('alsoInOtherCountries')}</OtherBrandsTitle>
          }
          <BrandGridWrapper columns={[2, 3, 3, 4]}>
            {otherGiftcards.map(brand => <BrandCard key={brand.id} brand={brand} />)}
          </BrandGridWrapper>
        </OtherBrands>
      }

      {!loading && showOtherBrands && !!otherTopups.length &&
        <OtherBrands>
          {(!!otherGiftcards.length || !!brands.length) &&
            <OtherBrandsTitle>
              {(!brands.length || (!!otherGiftcards.length && !!brands.length)) && t('andMobileTopups')}
              {!otherGiftcards.length && t('alsoMobileTopups')}
            </OtherBrandsTitle>
          }
          <BrandGridWrapper columns={[2, 3, 3, 4]}>
            {otherTopups.map(brand => <BrandCard key={brand.id} brand={brand} />)}
          </BrandGridWrapper>
        </OtherBrands>
      }
    </Container>
  );
};

export default BrandGrid;
